/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { BlocksContent } from "@strapi/blocks-react-renderer";
import { Memoize } from "typescript-memoize";
import { NewRelicDecoratedError } from "utilities/noticeError";

export enum PublicationState {
  preview = "preview",
  live = "live",
}

export enum SortBy {
  updatedAtDesc = "updatedAt:desc",
}

export enum CmsPageType {
  guides = "guide-pages",
  creator = "creator-pages",
}

export interface CmsResponse<A> {
  data: A[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export type CmsAttributes<A> = A & {
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  publishedAt: string | null;
};

export interface CmsResource<A> {
  id: number;
  attributes: CmsAttributes<A>;
}

export interface CmsImage {
  data: CmsResource<CmsImageAttributes>;
}

export interface CmsImageFormat {
  url: string;
  width: number;
  height: number;
}

export interface CmsImageAttributes extends CmsImageFormat {
  caption: string | null;
  alternativeText: string | null;
  name: string;
  formats: Record<string, CmsImageFormat>;
}

export interface CmsCodeAttributes {
  type: string;
  url?: string;
  caption?: string;
}

export interface CmsPlayerAttributes {
  url: string;
  caption?: string;
}

export interface GuidePageAttributes {
  Title: string;
  Subtitle: string;
  Content: BlocksContent;
  Hero: CmsImage;
  pageTitle: string;
  metaDescription: string;
  slug: string;
  shortTitle: string;
  locale: string;
  localizations: {
    data: CmsResource<Pick<GuidePageAttributes, "locale" | "slug">>[];
  };
  tags?: { name: string }[];
}

export type GuidePage = CmsResource<GuidePageAttributes>;

export interface CreatorStep {
  Title: string;
  Description: string;
  Image: CmsImage;
  Link: string;
}

export interface CreatorSnippet {
  Title: string;
  Description: string;
  Image: CmsImage;
  CTALink: string;
  CTAText: string;
}

export interface CreatorFeature {
  Title: string;
  Description: BlocksContent;
  Image: CmsImage;
  CTALink: string;
  CTAText: string;
}

export interface CreatorPageAttributes {
  Title: string;
  Description: string;
  HeroImage: CmsImage;
  slug: string;
  tags?: { name: string }[];
  Blockquote: BlocksContent;
  ThreeStepsHeading: string;
  ThreeSteps: CreatorStep[];
  CTALink: string;
  SnippetsHeading: string;
  Snippets: CreatorSnippet[];
  Features: CreatorFeature[];
  CTAText: string;
  locale: string;
  localizations: {
    data: CmsResource<Pick<CreatorPageAttributes, "locale" | "slug">>[];
  };
}

export type CreatorPage = CmsResource<CreatorPageAttributes>;

// Generic page type for common operations
export type CmsPage = GuidePage | CreatorPage;
export type CmsPageAttributes = GuidePageAttributes | CreatorPageAttributes;

export type Tag = string;

export const AllPagesTag = "ALL_PAGES_TAG";

export class CmsClient {
  private readonly baseUrl: string;
  private readonly adobeIOApiKey: string;

  constructor(baseUrl: string, adobeIOApiKey: string) {
    this.baseUrl = baseUrl;
    this.adobeIOApiKey = adobeIOApiKey;
  }

  async getGuidePagesByTag(params: {
    locale: string;
    tag: Tag;
  }): Promise<GuidePage[]> {
    const guidePages = await this.getAllGuidePages({ locale: params.locale });
    return this.filterPagesByTag(guidePages, params.tag);
  }

  @Memoize()
  async getAllGuidePages(params: {
    locale: string;
    sortBy?: SortBy;
    count?: number;
  }): Promise<GuidePage[]> {
    const pages: GuidePage[] = [];
    let page = 1;
    let hasMore = true;
    while (hasMore) {
      const response = await this.getGuidePages(
        PublicationState.live,
        params.locale,
        params.sortBy,
        page
      );
      pages.push(...response.data);
      hasMore =
        response.meta.pagination.pageCount > page &&
        (params.count === undefined || pages.length < params.count);
      page++;
    }

    if (params.count !== undefined) {
      return pages.slice(0, params.count);
    } else {
      return pages;
    }
  }

  @Memoize()
  async getGuidePage(slug: string): Promise<GuidePage | undefined> {
    const [livePage, previewPage] = await Promise.all([
      this.getGuidePageInState(PublicationState.live, slug),
      this.getGuidePageInState(PublicationState.preview, slug),
    ]);

    return livePage ?? previewPage;
  }

  async getAllGuidePagesTags(params: { locale: string }): Promise<Tag[]> {
    const guidePages = await this.getAllGuidePages(params);
    return this.extractTagsFromPages(guidePages);
  }

  async getCreatorPagesByTag(params: {
    locale: string;
    tag: Tag;
  }): Promise<CreatorPage[]> {
    const creatorPages = await this.getAllCreatorPages({
      locale: params.locale,
    });
    return this.filterPagesByTag(creatorPages, params.tag);
  }

  @Memoize()
  async getAllCreatorPages(params: {
    locale: string;
    sortBy?: SortBy;
    count?: number;
  }): Promise<CreatorPage[]> {
    const pages: CreatorPage[] = [];
    let page = 1;
    let hasMore = true;
    while (hasMore) {
      const response = await this.getCreatorPages(
        PublicationState.live,
        params.locale,
        params.sortBy,
        page
      );
      pages.push(...response.data);
      hasMore =
        response.meta.pagination.pageCount > page &&
        (params.count === undefined || pages.length < params.count);
      page++;
    }

    if (params.count !== undefined) {
      return pages.slice(0, params.count);
    } else {
      return pages;
    }
  }

  @Memoize()
  async getCreatorPage(slug: string): Promise<CreatorPage | undefined> {
    const [livePage, previewPage] = await Promise.all([
      this.getCreatorPageInState(PublicationState.live, slug),
      this.getCreatorPageInState(PublicationState.preview, slug),
    ]);

    return livePage ?? previewPage;
  }

  async getAllCreatorPagesTags(params: { locale: string }): Promise<Tag[]> {
    const creatorPages = await this.getAllCreatorPages(params);
    return this.extractTagsFromPages(creatorPages);
  }

  private extractTagsFromPages(pages: CmsPage[]): Tag[] {
    const duplicateTags: string[] = pages
      .map((page) => page.attributes.tags?.map((tag) => tag.name))
      .filter((tag) => tag !== undefined)
      .flat();
    return [AllPagesTag, ...new Set(duplicateTags)].sort();
  }

  private filterPagesByTag<T extends CmsPage>(pages: T[], tag: Tag): T[] {
    if (tag === AllPagesTag) {
      return pages;
    } else {
      return pages.filter((page: T) => {
        return Boolean(page.attributes.tags?.find((t) => t.name === tag));
      });
    }
  }

  private async getGuidePages(
    publicationState: PublicationState,
    locale: string,
    sortBy: SortBy = SortBy.updatedAtDesc,
    page: number = 1
  ): Promise<CmsResponse<GuidePage>> {
    return await this.getCmsPages<GuidePage>(
      CmsPageType.guides,
      publicationState,
      locale,
      sortBy,
      page
    );
  }

  private async getCreatorPages(
    publicationState: PublicationState,
    locale: string,
    sortBy: SortBy = SortBy.updatedAtDesc,
    page: number = 1
  ): Promise<CmsResponse<CreatorPage>> {
    return await this.getCmsPages<CreatorPage>(
      CmsPageType.creator,
      publicationState,
      locale,
      sortBy,
      page
    );
  }

  private async getCmsPages<T>(
    pageType: CmsPageType,
    publicationState: PublicationState,
    locale: string,
    sortBy: SortBy = SortBy.updatedAtDesc,
    page: number = 1
  ): Promise<CmsResponse<T>> {
    const response = await this.baseFetch(
      `${pageType}?publicationState=${publicationState}&sort=${sortBy}&locale=${locale}&populate=*&pagination[page]=${page}`
    );
    return await response.json();
  }

  private async getPageInState<T>(
    pageType: CmsPageType,
    publicationState: PublicationState,
    slug: string,
    populate: string = "*"
  ): Promise<T | undefined> {
    const response = await this.baseFetch(
      `${pageType}?publicationState=${publicationState}&locale=all&populate=${populate}&filters[slug][$eq]=${encodeURI(
        slug
      )}`
    );
    const page: CmsResponse<T> = await response.json();
    return page.data[0];
  }

  private async getGuidePageInState(
    publicationState: PublicationState,
    slug: string
  ): Promise<GuidePage | undefined> {
    return this.getPageInState<GuidePage>(
      CmsPageType.guides,
      publicationState,
      slug
    );
  }

  private async getCreatorPageInState(
    publicationState: PublicationState,
    slug: string
  ): Promise<CreatorPage | undefined> {
    return this.getPageInState<CreatorPage>(
      CmsPageType.creator,
      publicationState,
      slug,
      `${["HeroImage", "ThreeSteps.Image", "Snippets.Image", "Features.Image", "localizations"]}`
    );
  }

  private baseFetch: typeof fetch = async (
    url: string,
    requestInfo?: RequestInit
  ): Promise<Response> => {
    const response = await fetch(`${this.baseUrl}/${url}`, {
      ...requestInfo,
      headers: {
        ["X-Api-Key"]: this.adobeIOApiKey,
      },
    });
    if (response.ok) {
      return response;
    } else {
      throw new CmsError("unexpected cms response", {
        status: response.status,
        url: url,
        method: requestInfo?.method ?? "GET",
      });
    }
  };
}

class CmsError extends NewRelicDecoratedError {
  constructor(
    error: Error | string,
    customAttributes: Record<string, newrelic.SimpleType> = {}
  ) {
    super(error, customAttributes);
    this.name = "CmsError;";
  }
}

export namespace CmsApi {
  export const instance = new CmsClient(
    `${process.env.PHONOS_GATEWAY_URL!}/api/v1/cms`,
    process.env.ADOBE_IO_API_KEY!
  );
}
